import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Alert,
    TextField,
} from '@mui/material';
import { getISOWeek, getYear } from 'date-fns';
import { exportTimesheets } from '../services/api';
import toast from 'react-hot-toast';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { makeRenderWeekDay } from '../components/datePicker/renderWeekDay';

export const ExportView: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<DateTime>(DateTime.now());
    const [hoveredDate, setHoveredDate] = useState<DateTime<true> | DateTime<false> | null>(null);

    const [weekNumber, setWeekNumber] = useState<string>(
        getISOWeek(new Date()).toString()
    );
    const [year, setYear] = useState<string>(getYear(new Date()).toString());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDateChange = (newValue: DateTime | null) => {
        if (newValue) {
            setSelectedDate(newValue);
            const jsDate = newValue.toJSDate();
            setWeekNumber(getISOWeek(jsDate).toString());
            setYear(getYear(jsDate).toString());
        }
    };

    const handleExport = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await exportTimesheets({ weekNumber, year });
            if (response.data.hasData === false) {
                toast.error("Aucune donnée a exporter pour le moment");
                setLoading(false);
                setError("Aucune donnée a exporter pour le moment");
                return;
            }
            const { filename, contentType, base64 } = response.data;

            const byteCharacters = atob(base64.replace(/\s/g, ''));
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: contentType });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();

            toast.success('Export réussi ! Le ZIP est disponible.');
        } catch (err: any) {
            setError(err.message || 'Export échoué');
            toast.error(err.message || 'Export échoué');
        }
        setLoading(false);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto' }}>
                <Typography variant="h5" gutterBottom>
                    Export des Timesheets
                </Typography>
                <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="fr">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DatePicker
                                label="Date"
                                format="dd/MM/yyyy"
                                value={selectedDate}
                                onChange={handleDateChange}
                                slots={{
                                    day: makeRenderWeekDay(
                                        hoveredDate,
                                        setHoveredDate,
                                        (dt) => dt.toJSDate() // convert Luxon DateTime to JS Date
                                    ),
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Semaine"
                                value={weekNumber}
                                InputProps={{ readOnly: true }}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Année"
                                value={year}
                                InputProps={{ readOnly: true }}
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleExport}
                                disabled={loading}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Exporter'}
                            </Button>
                        </Grid>
                        {error && (
                            <Grid item xs={12}>
                                <Alert severity="error">{error}</Alert>
                            </Grid>
                        )}
                    </Grid>
                </LocalizationProvider>
            </Paper>
        </Box>
    );
};
